import axios from "axios";
import config from "../../../config/config";
import { attachSessionInterceptors } from "../../../public/api/httpClient";
import { filterActiveMasterRows } from "../../memberships/utils/masterLookupUtils";

const TO_FRO_BASE = `${config.BASE_URL}/to-and-fro-register`;

const api = attachSessionInterceptors(
  axios.create({
    baseURL: "",
    headers: {
      "Content-Type": "application/json",
    },
  }),
);

// To & Fro API endpoints
export const toFroApi = {
  // Get movements with filters
  getMovements: async (filters = {}) => {
    const response = await api.get(TO_FRO_BASE, { params: filters });
    return response.data;
  },

  // Create new movement
  createMovement: async (movementData) => {
    const response = await api.post(TO_FRO_BASE, movementData);
    return response.data;
  },

  // Get single movement
  getMovement: async (id) => {
    const response = await api.get(`${TO_FRO_BASE}/${id}`);
    return response.data;
  },

  // Update movement
  updateMovement: async (id, movementData) => {
    const response = await api.put(`${TO_FRO_BASE}/${id}`, movementData);
    return response.data;
  },

  // Mark movement as returned
  markReturned: async (id, returnData) => {
    const response = await api.put(`${TO_FRO_BASE}/${id}/return`, returnData);
    return response.data;
  },

  // Delete movement
  deleteMovement: async (id) => {
    const response = await api.delete(`${TO_FRO_BASE}/${id}`);
    return response.data;
  },

  // Get movement stats
  getMovementStats: async () => {
    const response = await api.get(`${TO_FRO_BASE}/stats`);
    return response.data;
  },

  // Get master data (dropdowns)
  getMasterData: async () => {
    const response = await api.get(`${TO_FRO_BASE}/masters`);
    const payload = response.data;
    const raw = payload?.data ?? payload;
    if (!raw || typeof raw !== "object") return payload;
    return {
      ...payload,
      data: {
        ...raw,
        purposes: filterActiveMasterRows(raw.purposes),
      },
    };
  },

  /** Distinct directory column values from movement register (scoped for office users). */
  getMovementFilterOptions: async (column) => {
    const response = await api.get(`${TO_FRO_BASE}/filter-options`, {
      params: { column: String(column || "").trim() },
    });
    return response.data;
  },

  // Export report
  exportReport: async (format, filters = {}) => {
    try {
      const response = await api.get(`${TO_FRO_BASE}/export/${format}`, {
        params: filters,
        responseType: "blob",
      });
      
      // Force correct filename - completely ignore backend
      const baseFilename = `to-fro-report-${new Date().toISOString().split('T')[0]}`;
      let filename;
      
      if (format === 'excel') {
        filename = baseFilename + '.xlsx';
      } else if (format === 'csv') {
        filename = baseFilename + '.csv';
      } else {
        filename = baseFilename + '.txt';
      }
      
      // Create blob with correct MIME type
      const mimeType = format === 'excel' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv;charset=utf-8';
      
      const blob = new Blob([response.data], { type: mimeType });
      
      // Use window.open approach for some browsers
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // For IE/Edge
        window.navigator.msSaveOrOpenBlob(blob, filename);
        return { success: true, filename };
      }
      
      // Create download link with explicit filename setting
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      
      // Add to DOM and trigger
      document.body.appendChild(a);
      
      // Force the download attribute to be set
      a.setAttribute('download', filename);
      
      // Trigger click
      a.click();
      
      // Also try programmatically setting the download after click
      setTimeout(() => {
        a.setAttribute('download', filename);
        a.click();
      }, 50);
      
      // Cleanup
      setTimeout(() => {
        if (document.body.contains(a)) {
          document.body.removeChild(a);
        }
        window.URL.revokeObjectURL(url);
      }, 200);
      
      console.log('Download triggered with filename:', filename);
      return { success: true, filename };
      
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  },
};

export default toFroApi;
